import { z } from "zod/v4";
import { defineResource, defineEndpoint } from "@visioni/a3s-client-api";
import { Role, CameraId, CameraRole } from "../schemas";
import { UserIdSchema } from "@visioni/a3s-users";
import { FEATURE_NAME } from "../constants";

export const AccessManagementRoleAssignment = z.object({
  user: UserIdSchema,
  role: Role,
});
export type AccessManagementRoleAssignment = z.infer<typeof AccessManagementRoleAssignment>;

export const CreateRoleAssignmentRequest = AccessManagementRoleAssignment;
export type CreateRoleAssignmentRequest = z.infer<typeof CreateRoleAssignmentRequest>;

export const CreateRoleAssignmentResponse = AccessManagementRoleAssignment;
export type CreateRoleAssignmentResponse = z.infer<typeof CreateRoleAssignmentResponse>;

export const DeleteRoleAssignmentRequest = AccessManagementRoleAssignment;
export type DeleteRoleAssignmentRequest = z.infer<typeof DeleteRoleAssignmentRequest>;

/**
 * Query params schema for DELETE role assignment.
 * Uses string encoding for URL compatibility.
 */
export const DeleteRoleAssignmentQuery = z.object({
  user: UserIdSchema,
  role: Role,
});
export type DeleteRoleAssignmentQuery = z.infer<typeof DeleteRoleAssignmentQuery>;

export const DeleteRoleAssignmentResponse = z.strictObject({});
export type DeleteRoleAssignmentResponse = z.infer<typeof DeleteRoleAssignmentResponse>;

export const RevokeUserAccessQuery = z.object({
  user: UserIdSchema,
});
export type RevokeUserAccessQuery = z.infer<typeof RevokeUserAccessQuery>;

/**
 * Result of revoking a user's access.
 * - `revokedRoles` holds grouping-policy values (tenant admin, site manager, etc.)
 * - `revokedCameraPolicies` holds camera permission values (camera:S0-site1/asset1@cam1, etc.)
 */
export const RevokeUserAccessResponse = z.object({
  revokedRoles: z.array(z.string()),
  revokedCameraPolicies: z.array(z.string()),
});
export type RevokeUserAccessResponse = z.infer<typeof RevokeUserAccessResponse>;

export const AccessManagementUser = z.object({
  id: UserIdSchema,
  email: z.string(),
  givenName: z.string().optional(),
  familyName: z.string().optional(),
  phoneNumber: z.string().optional(),
});
export type AccessManagementUser = z.infer<typeof AccessManagementUser>;

export const AccessManagementResponse = z.object({
  users: z.array(AccessManagementUser),
  roles: z.array(AccessManagementRoleAssignment),
  cameras: z.array(
    z.object({
      user: UserIdSchema,
      camera: z.string(),
      role: CameraRole,
    }),
  ),
});
export type AccessManagementResponse = z.infer<typeof AccessManagementResponse>;

// ==================== Camera Permission Schemas ====================

const CameraIdCodec = z.codec(z.string(), z.custom<CameraId>(), {
  decode: (val) => CameraId.parse(val),
  encode: (val) => val as string,
});

export const CameraPermissionAssignment = z.object({
  user: UserIdSchema,
  camera: CameraIdCodec,
  role: CameraRole,
});
export type CameraPermissionAssignment = z.infer<typeof CameraPermissionAssignment>;

export const AddCameraPermissionRequest = CameraPermissionAssignment;
export type AddCameraPermissionRequest = z.infer<typeof AddCameraPermissionRequest>;

export const AddCameraPermissionResponse = CameraPermissionAssignment;
export type AddCameraPermissionResponse = z.infer<typeof AddCameraPermissionResponse>;

export const DeleteCameraPermissionRequest = z.object({
  user: UserIdSchema,
  camera: CameraIdCodec,
});
export type DeleteCameraPermissionRequest = z.infer<typeof DeleteCameraPermissionRequest>;

export const DeleteCameraPermissionQuery = z.object({
  user: UserIdSchema,
  camera: CameraIdCodec,
});
export type DeleteCameraPermissionQuery = z.infer<typeof DeleteCameraPermissionQuery>;

export const DeleteCameraPermissionResponse = z.strictObject({});
export type DeleteCameraPermissionResponse = z.infer<typeof DeleteCameraPermissionResponse>;

export const InviteUserRequest = z.object({
  email: z.email(),
  /** @deprecated Use `roles` instead. Kept for backward compatibility with older clients. */
  role: Role.optional(),
  roles: z.array(Role).optional(),
  cameraPermissions: z.array(z.object({ camera: CameraIdCodec, role: CameraRole })).optional(),
});
export type InviteUserRequest = z.infer<typeof InviteUserRequest>;

export const InviteUserResponse = z.object({
  user: UserIdSchema,
  email: z.string(),
  /** @deprecated Use `roles` instead. Kept for backward compatibility with older clients. */
  role: Role.optional(),
  roles: z.array(Role),
  cameraPermissions: z.array(z.object({ camera: CameraIdCodec, role: CameraRole })),
});
export type InviteUserResponse = z.infer<typeof InviteUserResponse>;

// ==================== User Search Schemas ====================

export const UsersSearchQuery = z.object({
  q: z.string(),
});
export type UsersSearchQuery = z.infer<typeof UsersSearchQuery>;

export const UsersSearchResult = z.object({
  auth0Id: z.string(),
  email: z.string(),
  name: z.string().optional(),
  legacyId: z.string().optional(),
});

export const UsersSearchResponse = z.object({
  users: z.array(UsersSearchResult),
});
export type UsersSearchResponse = z.infer<typeof UsersSearchResponse>;

// ==================== Resource & Endpoint Definitions ====================

export const accessManagementResource = defineResource({
  feature: FEATURE_NAME,
  resource: "manage",
  scope: "tenant",
});

export const accessManagementEndpoint = defineEndpoint(accessManagementResource, {
  responseSchema: AccessManagementResponse,
});

export const roleAssignmentResource = defineResource({
  feature: FEATURE_NAME,
  resource: "manage/role",
  scope: "tenant",
});

export const createRoleAssignmentEndpoint = defineEndpoint(roleAssignmentResource, {
  method: "POST",
  bodySchema: CreateRoleAssignmentRequest,
  responseSchema: CreateRoleAssignmentResponse,
});

export const deleteRoleAssignmentEndpoint = defineEndpoint(roleAssignmentResource, {
  method: "DELETE",
  querySchema: DeleteRoleAssignmentQuery,
  responseSchema: DeleteRoleAssignmentResponse,
});

export const inviteUserResource = defineResource({
  feature: FEATURE_NAME,
  resource: "manage/invite",
  scope: "tenant",
});

export const inviteUserEndpoint = defineEndpoint(inviteUserResource, {
  method: "POST",
  bodySchema: InviteUserRequest,
  responseSchema: InviteUserResponse,
});

export const cameraPermissionResource = defineResource({
  feature: FEATURE_NAME,
  resource: "manage/camera",
  scope: "tenant",
});

export const addCameraPermissionEndpoint = defineEndpoint(cameraPermissionResource, {
  method: "PUT",
  bodySchema: AddCameraPermissionRequest,
  responseSchema: AddCameraPermissionResponse,
});

export const deleteCameraPermissionEndpoint = defineEndpoint(cameraPermissionResource, {
  method: "DELETE",
  querySchema: DeleteCameraPermissionQuery,
  responseSchema: DeleteCameraPermissionResponse,
});

export const revokeUserAccessResource = defineResource({
  feature: FEATURE_NAME,
  resource: "manage/user",
  scope: "tenant",
});

export const revokeUserAccessEndpoint = defineEndpoint(revokeUserAccessResource, {
  method: "DELETE",
  querySchema: RevokeUserAccessQuery,
  responseSchema: RevokeUserAccessResponse,
});

export const usersSearchResource = defineResource({
  feature: FEATURE_NAME,
  resource: "users/search",
  scope: "tenant",
});

export const usersSearchEndpoint = defineEndpoint(usersSearchResource, {
  querySchema: UsersSearchQuery,
  responseSchema: UsersSearchResponse,
});

// ==================== Set User Permissions Schemas ====================

export const SetUserPermissionsRequest = z.object({
  user: UserIdSchema,
  roles: z.array(Role),
  cameraPermissions: z.array(z.object({ camera: CameraIdCodec, role: CameraRole })),
});
export type SetUserPermissionsRequest = z.infer<typeof SetUserPermissionsRequest>;

export const SetUserPermissionsResponse = z.object({
  user: UserIdSchema,
  roles: z.array(Role),
  cameraPermissions: z.array(z.object({ camera: CameraIdCodec, role: CameraRole })),
  skippedLegacyCameras: z.array(z.string()),
});
export type SetUserPermissionsResponse = z.infer<typeof SetUserPermissionsResponse>;

export const setUserPermissionsResource = defineResource({
  feature: FEATURE_NAME,
  resource: "manage/user-permissions",
  scope: "tenant",
});

export const setUserPermissionsEndpoint = defineEndpoint(setUserPermissionsResource, {
  method: "PUT",
  bodySchema: SetUserPermissionsRequest,
  responseSchema: SetUserPermissionsResponse,
});
